import React, { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { API_BASE } from "../helpers/api";

export default function QuoteReview() {
  const { reviewToken } = useParams();
  const { state } = useLocation();
  const quote = state?.quote;
  const [csrfToken, setCsrfToken] = useState("");
  const [form, setForm] = useState({
    name: "",
    email: state?.email || "",
    phone: "",
    contact_preference: "email",
    details: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    fetch(`${API_BASE}/csrf/`, { credentials: "include" })
      .then((response) => response.json())
      .then((data) => setCsrfToken(data.csrfToken || ""))
      .catch(() => setError("The form could not be initialised. Please refresh and try again."));
  }, []);

  const update = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const submit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const response = await fetch(`${API_BASE}/cutting/review/${reviewToken}/`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": csrfToken,
        },
        body: JSON.stringify(form),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.detail || "Unable to submit this quote for review.");
      setSubmitted(true);
    } catch (err) {
      setError(err.message || "Unable to submit this quote for review.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass =
    "mt-2 w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-slate-900 outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-200 dark:border-slate-600 dark:bg-slate-900 dark:text-white";

  if (submitted) {
    return (
      <section className="mx-auto max-w-2xl rounded-3xl bg-white p-8 text-slate-900 shadow-xl dark:bg-slate-900 dark:text-white md:p-12">
        <p className="text-sm font-bold uppercase tracking-widest text-green-600">Review requested</p>
        <h1 className="mt-3 text-3xl font-black">Thanks — we’ll take a closer look.</h1>
        <p className="mt-4 text-slate-600 dark:text-slate-300">
          Quote #{quote?.id || ""} and your additional requirements have been submitted. We’ll contact you by {form.contact_preference === "phone" ? "phone" : "email"} to discuss the work and confirm updated pricing.
        </p>
        <Link to="/" className="mt-8 inline-block font-bold text-orange-600 hover:underline">Return home</Link>
      </section>
    );
  }

  return (
    <section className="mx-auto max-w-3xl overflow-hidden rounded-3xl bg-white text-slate-900 shadow-xl dark:bg-slate-900 dark:text-white">
      <div className="bg-slate-950 px-6 py-8 text-white md:px-10">
        <p className="text-sm font-bold uppercase tracking-widest text-orange-400">Quote {quote?.id ? `#${quote.id}` : "review"}</p>
        <h1 className="mt-2 text-3xl font-black">Request additional work</h1>
        <p className="mt-3 max-w-2xl text-slate-300">
          Tell us about tapping, countersinking, folding, welding, finishing, tighter tolerances, or anything else this part needs. We’ll review the drawing and contact you with an updated quote.
        </p>
        {quote && (
          <p className="mt-4 text-sm font-semibold text-slate-200">
            {quote.material} · {quote.thickness_mm} mm · {quote.width_mm} × {quote.height_mm} mm · current estimate A${(quote.price_cents / 100).toFixed(2)}
          </p>
        )}
      </div>

      <form onSubmit={submit} className="space-y-6 p-6 md:p-10">
        <label className="block text-sm font-semibold">
          Your name
          <input className={inputClass} name="name" value={form.name} onChange={update} required maxLength="200" autoComplete="name" />
        </label>

        <fieldset>
          <legend className="text-sm font-semibold">How would you like us to contact you?</legend>
          <div className="mt-3 flex flex-wrap gap-3">
            {[["email", "Email"], ["phone", "Phone call"]].map(([value, label]) => (
              <label key={value} className="flex cursor-pointer items-center gap-2 rounded-lg border border-slate-300 px-4 py-3 dark:border-slate-600">
                <input type="radio" name="contact_preference" value={value} checked={form.contact_preference === value} onChange={update} className="accent-orange-500" />
                {label}
              </label>
            ))}
          </div>
        </fieldset>

        <div className="grid gap-5 md:grid-cols-2">
          <label className="block text-sm font-semibold">
            Email address {form.contact_preference === "email" && <span className="text-orange-600">*</span>}
            <input className={inputClass} type="email" name="email" value={form.email} onChange={update} required={form.contact_preference === "email"} autoComplete="email" />
          </label>
          <label className="block text-sm font-semibold">
            Phone number {form.contact_preference === "phone" && <span className="text-orange-600">*</span>}
            <input className={inputClass} type="tel" name="phone" value={form.phone} onChange={update} required={form.contact_preference === "phone"} maxLength="50" autoComplete="tel" />
          </label>
        </div>

        <label className="block text-sm font-semibold">
          What else would you like done?
          <textarea
            className={inputClass}
            name="details"
            value={form.details}
            onChange={update}
            required
            maxLength="5000"
            rows="7"
            placeholder="For example: Tap the four 5 mm holes with M6 threads, and countersink the two mounting holes on the front face."
          />
          <span className="mt-1 block text-right text-xs text-slate-500">{form.details.length}/5000</span>
        </label>

        {error && <div className="rounded-lg bg-red-100 px-4 py-3 text-sm text-red-700 dark:bg-red-950 dark:text-red-200">{error}</div>}

        <div className="flex flex-wrap items-center gap-5">
          <button type="submit" disabled={!csrfToken || submitting} className="rounded-lg bg-orange-500 px-6 py-3 font-black text-slate-950 hover:bg-orange-400 disabled:cursor-not-allowed disabled:opacity-50">
            {submitting ? "Submitting…" : "Submit quote for review"}
          </button>
          <Link to={quote?.process === "plasma" ? "/plasma-cutting" : "/laser-cutting"} className="font-bold text-slate-500 hover:underline dark:text-slate-300">Back to estimate</Link>
        </div>
      </form>
    </section>
  );
}
